import React from 'react';
import type { CalendarEvent } from '../types/calendar.types.ts';
import {
  getMonthMatrix,
  isSameDay,
  toDateKey,
} from '../utils/calendarUtils.ts';

const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const COLOR_CLASS: Record<CalendarEvent['color'], string> = {
  purple: 'bg-purple-600/80 border-purple-400/50 text-white',
  green: 'bg-green-600/80 border-green-400/50 text-white',
  blue: 'bg-blue-600/80 border-blue-400/50 text-white',
  amber: 'bg-amber-500/90 border-amber-300/50 text-white',
  gray: 'bg-slate-600/60 border-slate-500/40 text-gray-300',
};

type CalendarMonthGridProps = {
  viewDate: Date;
  selectedDate: Date;
  eventsByDate: Map<string, CalendarEvent[]>;
  onSelectDate: (date: Date) => void;
  onSelectEvent: (event: CalendarEvent) => void;
};

const CalendarMonthGrid: React.FC<CalendarMonthGridProps> = ({
  viewDate,
  selectedDate,
  eventsByDate,
  onSelectDate,
  onSelectEvent,
}) => {
  const weeks = getMonthMatrix(viewDate);
  const today = new Date();
  const month = viewDate.getMonth();

  return (
    <div className="bg-slate-800/50 border border-white/10 rounded-xl overflow-hidden">
      <div className="grid grid-cols-7 border-b border-white/10 bg-slate-900/40">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="divide-y divide-white/5">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 divide-x divide-white/5 min-h-[100px] sm:min-h-[120px]">
            {week.map((day) => {
              const key = toDateKey(day);
              const dayEvents = eventsByDate.get(key) ?? [];
              const inMonth = day.getMonth() === month;
              const isToday = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelectDate(day)}
                  className={`p-1.5 sm:p-2 text-left transition hover:bg-white/5 focus:outline-none focus:ring-1 focus:ring-purple-500/50 ${
                    !inMonth ? 'opacity-40' : ''
                  } ${isSelected ? 'bg-purple-600/15 ring-1 ring-inset ring-purple-500/40' : ''}`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium mb-1 ${
                      isToday
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-300'
                    }`}
                  >
                    {day.getDate()}
                  </span>

                  <div className="space-y-0.5 hidden sm:block">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectEvent(event);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.stopPropagation();
                            onSelectEvent(event);
                          }
                        }}
                        className={`truncate text-[10px] leading-tight px-1.5 py-0.5 rounded border ${COLOR_CLASS[event.color]}`}
                        title={event.title}
                      >
                        {event.start.toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <p className="text-[10px] text-gray-500 pl-1">+{dayEvents.length - 3} daha</p>
                    )}
                  </div>

                  {dayEvents.length > 0 && (
                    <div className="flex gap-0.5 mt-1 sm:hidden justify-center">
                      {dayEvents.slice(0, 4).map((event) => (
                        <span
                          key={event.id}
                          className={`w-1.5 h-1.5 rounded-full ${
                            event.color === 'green'
                              ? 'bg-green-400'
                              : event.color === 'blue'
                                ? 'bg-blue-400'
                                : event.color === 'amber'
                                  ? 'bg-amber-400'
                                  : event.color === 'gray'
                                    ? 'bg-slate-400'
                                    : 'bg-purple-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarMonthGrid;
